import React, { useMemo, useState } from 'react'
import { useLuminaires } from '../../luminaire/hooks/useLuminaires'
import { useAssignedDevEuis } from '../hooks/useFleets'
import { FleetUiStrings } from '../i18n/fleetI18n'
import ButtonV2 from '../../../common/components/atom/ButtonV2/Button'

interface LuminairePickerProps {
  currentFleetId?: string
  currentMembers: string[]
  onAdd: (devEui: string) => void
  strings: FleetUiStrings
}

const rowStyle: React.CSSProperties = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  padding: '6px 8px',
  borderBottom: '1px solid #f1f5f9',
  fontSize: 13,
}

// Picker that lists luminaires NOT already in any fleet (1-fleet membership,
// plan §4 F2). Already-assigned luminaires are shown disabled with a hint;
// the server also rejects duplicates.
const LuminairePicker: React.FC<LuminairePickerProps> = ({
  currentFleetId,
  currentMembers,
  onAdd,
  strings,
}) => {
  const [search, setSearch] = useState('')
  const { luminairesQuery } = useLuminaires({ pageSize: 500 })
  const assigned = useAssignedDevEuis(currentFleetId)
  const memberSet = useMemo(() => new Set(currentMembers), [currentMembers])

  // Mémoïsé : `?? []` crée une nouvelle référence à chaque rendu, ce qui ferait
  // ré-exécuter le useMemo `filtered` en boucle (react-hooks/exhaustive-deps).
  const luminaires = useMemo(
    () => luminairesQuery.data?.data ?? [],
    [luminairesQuery.data],
  )

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase()
    return luminaires.filter((l) => {
      if (memberSet.has(l.lum_dev_eui)) return false // already in this fleet
      if (q === '') return true
      return (
        (l.lum_name || '').toLowerCase().includes(q) ||
        (l.lum_dev_eui || '').toLowerCase().includes(q)
      )
    })
  }, [luminaires, search, memberSet])

  return (
    <div
      style={{
        border: '1px solid #e5e7eb',
        borderRadius: 6,
        maxWidth: 420,
      }}
    >
      <div style={{ padding: 8, borderBottom: '1px solid #e5e7eb' }}>
        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder={strings.searchLuminaire}
          style={{
            width: '100%',
            height: 30,
            padding: '2px 8px',
            border: '1px solid #d1d5db',
            borderRadius: 4,
            fontSize: 13,
          }}
        />
      </div>
      <div style={{ maxHeight: 240, overflowY: 'auto' }}>
        {luminairesQuery.isLoading && (
          <div style={{ ...rowStyle, color: '#6b7280' }}>…</div>
        )}
        {!luminairesQuery.isLoading && filtered.length === 0 && (
          <div style={{ ...rowStyle, color: '#6b7280' }}>—</div>
        )}
        {filtered.map((l) => {
          const isAssignedElsewhere = assigned.has(l.lum_dev_eui)
          return (
            <div key={l.lum_dev_eui} style={rowStyle}>
              <span style={{ color: isAssignedElsewhere ? '#9ca3af' : '#1f2937' }}>
                {l.lum_name || l.lum_dev_eui}
                <span style={{ color: '#9ca3af', marginLeft: 6 }}>
                  {l.lum_dev_eui}
                </span>
              </span>
              {isAssignedElsewhere ? (
                <span style={{ fontSize: 11, color: '#9ca3af' }}>
                  {strings.alreadyAssigned}
                </span>
              ) : (
                <ButtonV2
                  type="button"
                  onClick={() => onAdd(l.lum_dev_eui)}
                  variant="contained"
                  color="primary"
                  size="small"
                >
                  +
                </ButtonV2>
              )}
            </div>
          )
        })}
      </div>
    </div>
  )
}

export default LuminairePicker
